import { useState } from "react";
import axios from "../axios";
import { Link, useNavigate } from "react-router-dom";
import BackButton from "../components/BackButton";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Styles partagés
const s = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f9fafb",
    fontFamily: "system-ui, -apple-system, sans-serif",
    padding: "24px",
  },
  card: {
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    padding: "36px 32px",
    width: "100%",
    maxWidth: "440px",
  },
  title: { fontSize: "22px", fontWeight: 700, color: "#1e293b", marginBottom: "8px" },
  subtitle: { fontSize: "13px", color: "#64748b", marginBottom: "28px", lineHeight: 1.6 },
  label: { display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "6px" },
  input: {
    width: "100%", padding: "11px 14px", border: "1.5px solid #CBD5E1",
    borderRadius: "8px", fontSize: "14px", background: "#F8FAFC", color: "#1e293b",
    boxSizing: "border-box", outline: "none", marginBottom: "16px", display: "block",
  },
  textarea: {
    width: "100%", padding: "11px 14px", border: "1.5px solid #CBD5E1",
    borderRadius: "8px", fontSize: "14px", background: "#F8FAFC", color: "#1e293b",
    boxSizing: "border-box", outline: "none", marginBottom: "16px", display: "block",
    resize: "vertical", minHeight: "80px", fontFamily: "inherit", lineHeight: 1.6,
  },
  btn: {
    width: "100%", padding: "13px", background: "#e11d48", color: "white",
    border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: 700,
    cursor: "pointer", marginBottom: "16px",
  },
  error: {
    background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "8px",
    padding: "10px 14px", color: "#dc2626", fontSize: "13px", marginBottom: "16px",
  },
  back: {
    background: "none", border: "none", color: "#e11d48", fontWeight: 600,
    fontSize: "13px", cursor: "pointer", padding: "0", marginBottom: "20px",
    display: "flex", alignItems: "center", gap: "4px",
  },
};

// ── Formulaire email (mot de passe oublié) ─────────────────────────────────
function EmailFlow({ onBack }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      toast.error("Erreur serveur, veuillez réessayer.");
    }
  };

  if (sent) {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>📧</div>
        <h2 style={{ color: "#166534", marginBottom: "8px" }}>Email envoyé !</h2>
        <p style={{ color: "#475569", fontSize: "14px", marginBottom: "24px" }}>
          Si cet email est associé à un compte, vous recevrez un lien dans quelques minutes.
        </p>
        <Link to="/login" style={{ color: "#e11d48", fontWeight: 600, textDecoration: "none" }}>
          Retour à la connexion
        </Link>
      </div>
    );
  }

  return (
    <>
      <button type="button" style={s.back} onClick={onBack}>← Retour</button>
      <h1 style={s.title}>Mot de passe oublié</h1>
      <p style={s.subtitle}>
        Entrez votre email et nous vous enverrons un lien de réinitialisation valable 1 heure.
      </p>
      <form onSubmit={handleSubmit}>
        <label style={s.label}>Adresse email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="votre@email.fr"
          required
          style={s.input}
        />
        <button type="submit" style={s.btn}>Envoyer le lien</button>
      </form>
      <p style={{ textAlign: "center", fontSize: "13px", color: "#64748b" }}>
        <Link to="/login" style={{ color: "#e11d48", textDecoration: "none", fontWeight: 600 }}>
          Retour à la connexion
        </Link>
      </p>
    </>
  );
}

// ── Formulaire pseudo (12 mots) ────────────────────────────────────────────
function PseudoFlow({ onBack }) {
  const [username, setUsername] = useState("");
  const [recoveryPhrase, setRecoveryPhrase] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/recover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          recovery_phrase: recoveryPhrase.trim(),
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || `Erreur ${res.status}`); return; }
      setSuccess(true);
    } catch {
      setError("Erreur réseau. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
        <p style={{ fontSize: "16px", fontWeight: 600, color: "#16a34a", marginBottom: "8px" }}>
          Mot de passe réinitialisé.
        </p>
        <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "24px" }}>
          Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
        </p>
        <Link
          to="/login"
          style={{ ...s.btn, display: "inline-block", textDecoration: "none",
                   textAlign: "center", padding: "12px 32px", width: "auto" }}
        >
          Se connecter
        </Link>
      </div>
    );
  }

  return (
    <>
      <button type="button" style={s.back} onClick={onBack}>← Retour</button>
      <h1 style={s.title}>Récupérer mon compte</h1>
      <p style={s.subtitle}>
        Saisissez votre pseudonyme, vos 12 mots de récupération et choisissez un nouveau mot de passe.
      </p>

      {error && <div style={s.error}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <label style={s.label}>Pseudonyme</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Votre pseudonyme"
          required
          style={s.input}
        />

        <label style={s.label}>Vos 12 mots de récupération</label>
        <textarea
          value={recoveryPhrase}
          onChange={(e) => setRecoveryPhrase(e.target.value)}
          placeholder="mot1 mot2 mot3 … mot12"
          required
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          style={s.textarea}
        />

        <label style={s.label}>Nouveau mot de passe</label>
        <div style={{ position: "relative", marginBottom: "16px" }}>
          <input
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Nouveau mot de passe"
            required
            minLength={6}
            style={{ ...s.input, marginBottom: 0, paddingRight: "44px" }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute", right: "12px", top: "50%",
              transform: "translateY(-50%)", background: "none", border: "none",
              cursor: "pointer", color: "#94a3b8", fontSize: "18px", padding: "0",
            }}
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{ ...s.btn, opacity: loading ? 0.65 : 1, cursor: loading ? "not-allowed" : "pointer" }}
        >
          {loading ? "Vérification…" : "Réinitialiser le mot de passe"}
        </button>
      </form>

      <p style={{ textAlign: "center", fontSize: "13px", color: "#64748b" }}>
        <Link to="/login" style={{ color: "#e11d48", textDecoration: "none", fontWeight: 600 }}>
          Retour à la connexion
        </Link>
      </p>
    </>
  );
}

// ── Page principale ────────────────────────────────────────────────────────
export default function RecoverAccountPage() {
  const [mode, setMode] = useState(null); // null | "email" | "pseudo"

  return (
    <div style={s.wrapper}>
      <ToastContainer theme="colored" />
      <BackButton />
      <div style={s.card}>
        {mode === "email" && <EmailFlow onBack={() => setMode(null)} />}
        {mode === "pseudo" && <PseudoFlow onBack={() => setMode(null)} />}

        {mode === null && (
          <>
            <h1 style={s.title}>Récupérer mon compte</h1>
            <p style={s.subtitle}>Comment avez-vous créé votre compte ?</p>

            <button
              type="button"
              onClick={() => setMode("email")}
              style={{
                width: "100%", padding: "18px 20px", marginBottom: "14px",
                background: "#fff", border: "2px solid #e11d48", borderRadius: "12px",
                cursor: "pointer", textAlign: "left", display: "flex",
                alignItems: "center", gap: "16px",
              }}
            >
              <span style={{ fontSize: "32px" }}>📧</span>
              <div>
                <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>
                  Avec un email
                </div>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                  Je recevrai un lien de réinitialisation
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setMode("pseudo")}
              style={{
                width: "100%", padding: "18px 20px", marginBottom: "24px",
                background: "#fff", border: "2px solid #CBD5E1", borderRadius: "12px",
                cursor: "pointer", textAlign: "left", display: "flex",
                alignItems: "center", gap: "16px",
              }}
            >
              <span style={{ fontSize: "32px" }}>🔑</span>
              <div>
                <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>
                  Sans email (pseudonyme)
                </div>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                  J'utiliserai mes 12 mots de récupération
                </div>
              </div>
            </button>

            <p style={{ textAlign: "center", fontSize: "13px", color: "#64748b" }}>
              <Link to="/login" style={{ color: "#e11d48", textDecoration: "none", fontWeight: 600 }}>
                Retour à la connexion
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
